import { Button, ButtonColor, ButtonSize } from "@swivl-mono/ui";
import { ReactNode } from "react";
import { BiEditAlt } from "react-icons/bi";

import { useTopicContext } from "../Hooks/useTopicContext";

interface TopicCardProps {
  title: string;
  description?: string;
  children: ReactNode;
}

/**
 * Card shell for the typed-setting topics embedded in the Policies page (gate code,
 * move out, profanity handling, lead creation). Same grammar as the policy card and
 * Details v3's single-card topics: title and caption on the left, Edit on the right
 * swapping for Cancel + Save while editing, a rule under the header, then the rows.
 *
 * Save and Cancel come from the topic context, so the topic keeps its own save path.
 * Only meaningful for topics rendered with `sectionEditing`; an auto-save topic has no
 * edit step to gate, and its header shows nothing on the right.
 */
export function TopicCard({ title, description, children }: TopicCardProps): React.ReactElement {
  const ctx = useTopicContext();

  return (
    <section className="overflow-hidden rounded-lg border border-grey-200 bg-white">
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <h3 className="heading-base-alt text-grey-900">{title}</h3>
          {description && <p className="body-sm mt-0.5 text-grey-600">{description}</p>}
        </div>
        {ctx.explicitEditing &&
          (ctx.isEditing ? (
            <div className="flex shrink-0 items-center gap-2">
              <Button color={ButtonColor.WHITE} size={ButtonSize.SMALL} disabled={ctx.saving} onClick={ctx.cancelEdits}>
                Cancel
              </Button>
              <Button color={ButtonColor.ORANGE} size={ButtonSize.SMALL} disabled={ctx.saving} onClickAsync={ctx.saveTopic}>
                {ctx.saving ? "Saving…" : "Save"}
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => ctx.setEditing(true)}
              className="body-sm-alt flex shrink-0 cursor-pointer items-center gap-2 text-grey-900 hover:text-orange-500"
            >
              Edit
              <BiEditAlt className="size-4" />
            </button>
          ))}
      </div>
      <div className="border-t border-grey-200 px-5 py-1">{children}</div>
    </section>
  );
}
